import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { environment } from '../../../environments/environment';
import { PieceHireQuote, PieceHireQuoteResponse } from '../../common/general-types/piece-hire-quote.dto';

/** One server quote for one application and one set of accepted channels. */
export interface PieceQuoteEntry {
  loading: boolean;
  response: PieceHireQuoteResponse | null;
  error: string | null;
}

/** Cache key for an application and the channels the brand has accepted, order-free. */
export function pieceQuoteKey(applicationId: string, acceptedChannelIds: readonly number[]): string {
  const channels = [...new Set(acceptedChannelIds.map(Number))].sort((a, b) => a - b);
  return `${applicationId}|${channels.join(',')}`;
}

/** The error `code` the API puts on a structured exception, e.g. `PRICE_CHANGED`. */
export function apiErrorCode(err: unknown): string | null {
  const body = (err as { error?: unknown } | null)?.error;
  if (body && typeof body === 'object' && typeof (body as { code?: unknown }).code === 'string') {
    return (body as { code: string }).code;
  }
  return null;
}

/** The re-priced quote a `409 PRICE_CHANGED` carries, or null for any other error. */
export function priceChangedQuote(err: unknown): PieceHireQuote | null {
  if (apiErrorCode(err) !== 'PRICE_CHANGED') {
    return null;
  }
  const details = (err as { error: { details?: { quote?: PieceHireQuote } } }).error.details;
  return details?.quote ?? null;
}

/**
 * Server hire quotes for piece-list briefs, shared by every surface that shows one.
 *
 * The drawer, the Review application modal, the Assignment table's Spend cell and the
 * hire itself all read the same cached quote, so they cannot disagree. Quotes are
 * cached per application and channel set, so ticking a channel back on is instant.
 */
@Injectable({ providedIn: 'root' })
export class PieceHireQuoteStore {
  private readonly http = inject(HttpClient);

  private readonly entries = signal<Record<string, PieceQuoteEntry>>({});
  /** The newest channel set asked for per application, so a stale answer is never shown as current. */
  private readonly latestKey = signal<Record<string, string>>({});
  /** The last quote that came back per application, shown while a newer one loads. */
  private readonly lastReady = signal<Record<string, PieceHireQuoteResponse>>({});
  /** Applications whose hire was refused with `409 PRICE_CHANGED`, until the brand acts again. */
  private readonly repriced = signal<ReadonlySet<string>>(new Set());

  /** A debounced request per application, and the channel set it was for. */
  private readonly timers = new Map<string, { timer: ReturnType<typeof setTimeout>; key: string }>();
  private readonly inFlight = new Map<string, Subscription>();

  /** The entry for exactly these channels, if one was asked for. */
  entryFor(applicationId: string, acceptedChannelIds: readonly number[]): PieceQuoteEntry | undefined {
    return this.entries()[pieceQuoteKey(applicationId, acceptedChannelIds)];
  }

  /** The quote for exactly these channels, or null until it has come back. */
  quoteFor(applicationId: string, acceptedChannelIds: readonly number[]): PieceHireQuoteResponse | null {
    return this.entryFor(applicationId, acceptedChannelIds)?.response ?? null;
  }

  /** The most recent quote for this application, whatever channels it was for. */
  lastQuoteFor(applicationId: string): PieceHireQuoteResponse | null {
    return this.lastReady()[applicationId] ?? null;
  }

  /** Whether the last hire attempt came back with a new price the brand has not confirmed. */
  priceChanged(applicationId: string): boolean {
    return this.repriced().has(applicationId);
  }

  markPriceChanged(applicationId: string): void {
    this.repriced.update((ids) => new Set(ids).add(applicationId));
  }

  /**
   * Make sure a quote for these channels is cached or on its way.
   *
   * `debounceMs` holds the request back while the brand is still clicking; a cached or
   * in-flight channel set is never asked for twice.
   */
  request(applicationId: string, acceptedChannelIds: readonly number[], debounceMs = 0): void {
    const key = pieceQuoteKey(applicationId, acceptedChannelIds);
    if (this.latestKey()[applicationId] !== key && this.repriced().has(applicationId)) {
      this.repriced.update((ids) => {
        const next = new Set(ids);
        next.delete(applicationId);
        return next;
      });
    }
    this.latestKey.update((latest) => ({ ...latest, [applicationId]: key }));

    this.cancelPending(applicationId);

    const existing = this.entries()[key];
    if (existing && (existing.loading || existing.response)) {
      return;
    }

    this.patch(key, { loading: true, response: null, error: null });
    if (debounceMs <= 0) {
      this.fetch(applicationId, key, acceptedChannelIds);
      return;
    }
    const timer = setTimeout(() => {
      this.timers.delete(applicationId);
      this.fetch(applicationId, key, acceptedChannelIds);
    }, debounceMs);
    this.timers.set(applicationId, { timer, key });
  }

  /** Store a quote the API handed back some other way, e.g. on `409 PRICE_CHANGED`. */
  accept(applicationId: string, acceptedChannelIds: readonly number[], response: PieceHireQuoteResponse): void {
    const key = pieceQuoteKey(applicationId, acceptedChannelIds);
    this.inFlight.get(key)?.unsubscribe();
    this.inFlight.delete(key);
    this.patch(key, { loading: false, response, error: null });
    this.lastReady.update((ready) => ({ ...ready, [applicationId]: response }));
  }

  /** Drop every quote for an application, e.g. once it is hired. */
  forget(applicationId: string): void {
    const prefix = `${applicationId}|`;
    this.cancelPending(applicationId);
    for (const [key, subscription] of this.inFlight) {
      if (key.startsWith(prefix)) {
        subscription.unsubscribe();
        this.inFlight.delete(key);
      }
    }
    this.entries.update((entries) =>
      Object.fromEntries(Object.entries(entries).filter(([key]) => !key.startsWith(prefix))),
    );
    this.latestKey.update(({ [applicationId]: _latest, ...rest }) => rest);
    this.lastReady.update(({ [applicationId]: _ready, ...rest }) => rest);
    this.repriced.update((ids) => {
      const next = new Set(ids);
      next.delete(applicationId);
      return next;
    });
  }

  /** A superseded debounced request never runs, so its placeholder entry goes too. */
  private cancelPending(applicationId: string): void {
    const pending = this.timers.get(applicationId);
    if (!pending) {
      return;
    }
    clearTimeout(pending.timer);
    this.timers.delete(applicationId);
    this.entries.update(({ [pending.key]: _dropped, ...rest }) => rest);
  }

  private fetch(applicationId: string, key: string, acceptedChannelIds: readonly number[]): void {
    const url = `${environment.shout_api.url}/brief-application/${applicationId}/quote`;
    const subscription = this.http
      .post<PieceHireQuoteResponse>(url, { acceptedCrossPlatforms: [...acceptedChannelIds].map(Number) })
      .subscribe({
        next: (response) => {
          this.inFlight.delete(key);
          this.patch(key, { loading: false, response, error: null });
          if (this.latestKey()[applicationId] === key) {
            this.lastReady.update((ready) => ({ ...ready, [applicationId]: response }));
          }
        },
        error: (err: unknown) => {
          this.inFlight.delete(key);
          const message =
            (err as { error?: { message?: unknown } } | null)?.error?.message;
          this.patch(key, {
            loading: false,
            response: null,
            error: typeof message === 'string' && message ? message : 'Could not price this hire',
          });
        },
      });
    this.inFlight.set(key, subscription);
  }

  private patch(key: string, entry: PieceQuoteEntry): void {
    this.entries.update((entries) => ({ ...entries, [key]: entry }));
  }
}
